<template>
  <b-modal no-fade v-model="dialog.show" :size="wizardMode ? 'lg' : 'xl'" :id="`modal-affiliate-${uniqueId}`"
           :hide-footer="true" centered @close="closeModal"
           no-close-on-backdrop
           dialog-class="am-dialog"
           body-class="am-body"
           header-class="am-modal-header"
  >
    <template #modal-title>
      <span v-if="isNewAffiliate">{{ $t('tabs.affiliate.newAffiliate') }}</span>
      <span v-else-if="loading !== true && error == null && affiliate != null">
        {{ displayName }}
      </span>
    </template>

    <!-- Wizard = compact; only the full detail view needs the big canvas -->
    <div class="am-content" :style="{ minHeight: wizardMode ? '0' : '620px' }">
      <!-- ERROR -->
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-if="loading !== true && error != null">
        <b-alert show variant="danger">
          <i class="fa-sharp fa-solid fa-triangle-exclamation"></i>
          {{ error }}
        </b-alert>
      </div>

      <!-- LOADING -->
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-else-if="loading === true">
        <b-spinner>{{ $t('operations.loading') }}</b-spinner>
      </div>

      <!-- CONTENT -->
      <template v-else>
        <!-- ============ NEW AFFILIATE — two-step wizard ============ -->
        <div v-if="wizardMode" class="am-new">
          <!-- Step indicator -->
          <div class="am-wiz">
            <div class="am-wiz-step" :class="{ 'is-active': wizard.step === 1, 'is-done': wizard.step > 1 }">
              <span class="am-wiz-dot">
                <i v-if="wizard.step > 1" class="fa-solid fa-check"></i><template v-else>1</template>
              </span>
              <span class="am-wiz-label">{{ $t('tabs.affiliate.details') }}</span>
            </div>
            <div class="am-wiz-line" :class="{ 'is-done': wizard.step > 1 }"></div>
            <div class="am-wiz-step" :class="{ 'is-active': wizard.step === 2 }">
              <span class="am-wiz-dot">2</span>
              <span class="am-wiz-label">{{ $t('affiliates.wizardCpaStep') }}</span>
            </div>
          </div>

          <!-- Step 1: affiliate details -->
          <div v-if="wizard.step === 1">
            <affiliate-edit-details ref="details" :new-affiliate="true" @submitted="onSubmitted" v-model="affiliate"/>
          </div>

          <!-- Step 2: optional price contract -->
          <div v-else class="am-wiz-cpa">
            <b-alert show variant="success" class="p-75 mb-1">
              <i class="fa-solid fa-circle-check mr-25"></i>{{ $t('affiliates.wizardCreated', { name: displayName }) }}
            </b-alert>
            <affiliate-cpa-form v-model="wizard.cpa" :affiliate-name="affiliate.company || affiliate.alias"/>
            <div class="am-wiz-actions">
              <b-button variant="outline-secondary" :disabled="wizard.saving" @click="finishWizard">
                {{ $t('affiliates.wizardSkip') }}
              </b-button>
              <b-button variant="primary" :disabled="wizardCpaInvalid || wizard.saving" @click="saveWizardCpa">
                <b-spinner v-if="wizard.saving" small class="mr-25"/>
                <i v-else class="fa-solid fa-tags mr-25"></i>{{ $t('affiliates.wizardSaveCpa') }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- ============ EXISTING AFFILIATE (hero + tabs) ============ -->
        <div v-else>
          <!-- ---------- COMPACT HERO ---------- -->
          <div class="am-hero">
            <div class="am-hero-row">
              <div class="am-avatar" :class="{ online: affiliate.active, offline: !affiliate.active }">
                <span class="am-avatar-initials">{{ avatarInitials }}</span>
                <span class="am-presence" v-b-tooltip.hover
                      :title="affiliate.active ? $t('operations.active') : $t('operations.inactive')"></span>
              </div>
              <div class="am-hero-name-block">
                <div class="am-hero-name">
                  <span class="am-hero-name-text">{{ displayName }}</span>
                  <span class="am-pill am-pill-ok" v-if="affiliate.active">
                    <i class="fa-solid fa-circle-check"></i> {{ $t('operations.active') }}
                  </span>
                  <span class="am-pill am-pill-off" v-else>
                    <i class="fa-solid fa-circle-xmark"></i> {{ $t('operations.inactive') }}
                  </span>
                </div>
                <div class="am-hero-sub">
                  <span v-if="affiliate.alias" class="am-sub-item"><i class="fa-solid fa-at"></i> {{ affiliate.alias }}</span>
                  <span v-if="affiliate.email" class="am-sub-item"><i class="fa-regular fa-envelope"></i> {{ affiliate.email }}</span>
                  <span v-if="affiliate.skype" class="am-sub-item"><i class="fa-brands fa-skype"></i> {{ affiliate.skype }}</span>
                  <span v-if="affiliate.id" class="am-sub-item"><i class="fa-regular fa-id-badge"></i> #{{ affiliate.id }}</span>
                  <span v-if="affiliate.creationTime" class="am-sub-item">
                    <i class="fa-regular fa-calendar"></i> {{ $moment(affiliate.creationTime).format('DD/MM/YYYY') }}
                  </span>
                </div>
              </div>
              <div class="am-stats" v-if="affiliate.currentCpa">
                <div class="am-stat am-stat-cpa">
                  <div class="am-stat-icon"><i class="fa-solid fa-money-bill-wave"></i></div>
                  <div>
                    <div class="am-stat-label">{{ $t('columns.cpa') }}</div>
                    <div class="am-stat-value">{{ convertToEURO(affiliate.currentCpa.cpa) }}</div>
                  </div>
                </div>
                <div class="am-stat am-stat-crg">
                  <div class="am-stat-icon"><i class="fa-solid fa-percent"></i></div>
                  <div>
                    <div class="am-stat-label">{{ $t('columns.crg') }}</div>
                    <div class="am-stat-value">{{ affiliate.currentCpa.crg != null ? affiliate.currentCpa.crg : '—' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ACTION BAR -->
            <div class="am-action-bar">
              <button class="am-quick-action" :disabled="!affiliate.email" @click="quickEmail"
                      :title="$t('operations.sendMail')">
                <i class="fa-solid fa-envelope"></i><span>{{ $t('operations.sendMail') }}</span>
              </button>
              <button class="am-quick-action" :disabled="!affiliate.email" @click="copyEmail"
                      :title="$t('columns.email')">
                <i class="fa-solid fa-copy"></i><span>{{ $t('columns.email') }}</span>
              </button>
              <div class="am-action-spacer"></div>
              <b-button variant="primary" size="sm" class="am-save-btn" @click="saveDetails">
                <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
              </b-button>
            </div>
          </div>

          <!-- ---------- TABS ---------- -->
          <b-tabs v-model="dialog.tab" content-class="am-tabs-content" nav-class="am-tabs-nav" class="am-tabs">
            <b-tab active>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-circle-info"></i> {{ $t('tabs.affiliate.details') }}</span>
              </template>
              <affiliate-edit-details ref="details" :new-affiliate="false" @submitted="onSubmitted" v-model="affiliate"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-key"></i> {{ $t('tabs.affiliate.connectionManagement') }}</span>
              </template>
              <affiliate-token-table :affiliateId="affiliate.id"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-tags"></i> {{ $t('tabs.affiliate.priceManagement') }}</span>
              </template>
              <affiliate-edit-cpa v-model="affiliate"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-clock"></i> {{ $t('tabs.affiliate.timeManagement') }}</span>
              </template>
              <affiliate-edit-times :affiliate-id="affiliate.id" v-model="affiliate.openingTime"/>
            </b-tab>
          </b-tabs>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import { mapActions } from 'vuex';
import AffiliateEditDetails from '@/components/partial/affiliates/AffiliateEditDetails';
import Affiliate from '@/model/affiliate/Affiliate';
import AffiliateEditCpa from '@/components/partial/affiliates/AffiliateEditCpa';
import AffiliateEditTimes from '@/components/partial/affiliates/AffiliateEditTimes';
import AffiliateTokenTable from '@/components/tables/AffiliateTokenTable.vue'
import AffiliateCpaForm from '@/components/partial/affiliates/AffiliateCpaForm.vue'

const initWizard = () => ({
  active: false,
  step: 1,
  cpa: { type: null, cpa: null, crg: null },
  saving: false,
})

export default {
  name: 'affiliateModal',
  components: { AffiliateTokenTable, AffiliateEditDetails, AffiliateEditCpa, AffiliateEditTimes, AffiliateCpaForm },
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    dialog: {
      show: false,
      tab: 0,

    },
    uniqueId: 0,
    loading: true,
    error: null,
    id: null,
    affiliate: {},
    // New-affiliate wizard: step 1 = details, step 2 = optional CPA contract.
    wizard: initWizard(),
  }),
  watch: {
    queryParams: {
      handler() {
        if (this.queryParams().affiliateId == null) {
          this.closeModal();
          return;
        }
        if (this.queryParams().affiliateId !== this.id) {
          this.showModal(this.queryParams().affiliateId);
        }
      },
      deep: true
    }
  },
  created() {
    this.uniqueId = this._uid;
    if (this.queryParams().affiliateId != null) {
      this.showModal(this.queryParams().affiliateId);
    }
  },
  computed: {
    isNewAffiliate(){
      return this.id == null || this.id === 0;
    },
    wizardMode(){
      return this.isNewAffiliate || this.wizard.active;
    },
    displayName(){
      if (this.affiliate == null) return '';
      return this.affiliate.company
          || this.affiliate.alias
          || this.affiliate.email
          || (this.affiliate.id != null ? `#${this.affiliate.id}` : '');
    },
    avatarInitials(){
      if (this.affiliate == null) return '?';
      const src = (this.affiliate.company || this.affiliate.alias || this.affiliate.email || '').trim();
      if (!src) return '?';
      const parts = src.split(/[\s@._-]+/).filter(Boolean);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts[1] ? parts[1][0] : '';
      return ((a + b) || a).toUpperCase() || '?';
    },
    wizardCpaInvalid(){
      const c = this.wizard.cpa;
      if (c.type == null) return true;
      if (c.cpa == null || c.cpa === '' || Number(c.cpa) <= 0) return true;
      if (c.type === 'CRG' && (c.crg == null || c.crg === '')) return true;
      return false;
    }
  },
  methods: {
    ...mapActions('affiliate', ['getAffiliateById', 'createCpaAffiliate']),
    showModal(id) {
      this.id = id;
      this.loading = true;
      this.dialog.show = true;
      this.dialog.tab = 0;
      this.wizard = initWizard();
      if (!this.isNewAffiliate) {
        this.refreshData();
      } else {
        this.affiliate = Affiliate();
        this.error = null;
        this.loading = false;
        this.wizard.active = true;
      }
      this.pushState({
        affiliateId: id,
        tab: 0,
      });
    },
    closeModal() {
      this.dialog.show = false;
      this.affiliate = {};
      this.wizard = initWizard();
      this.onDialogClose();
    },
    onDialogClose() {
      this.id = null;
      this.pushState({
        affiliateId: null,
        tab: null,
      });
    },
    refreshData() {
      let $this = this;
      this.getAffiliateById(this.id)
          .then(affiliate => {
            $this.affiliate = affiliate;
            $this.error = null;
            $this.loading = false;
          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
          });
    },
    onSubmitted(affiliate) {
      if (affiliate.id != null)
        this.id = affiliate.id;
      // Wizard flow: the affiliate exists now — continue to the optional CPA
      // step instead of jumping straight to the detail view.
      if (this.wizard.active) {
        this.affiliate = affiliate;
        this.wizard.step = 2;
        this.pushState({ affiliateId: this.id, tab: 0 });
        return;
      }
      this.refreshData()
      this.dialog.tab = 0;
    },
    finishWizard() {
      this.wizard = initWizard();
      this.loading = true;
      this.refreshData();
      this.dialog.tab = 0;
    },
    saveWizardCpa() {
      const $this = this;
      const c = this.wizard.cpa;
      const payload = {
        type: c.type,
        cpa: Number(c.cpa),
        crg: c.type === 'CRG' ? Number(c.crg) : null,
      };
      this.wizard.saving = true;
      this.createCpaAffiliate({ id: this.id, cpa: payload })
          .then(() => {
            $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.affiliates') }));
            $this.finishWizard();
          })
          .catch(err => {
            $this.showErrorNotification(
                $this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.affiliates') }),
                $this.getErrorDescription(err && err.error ? err.error : err));
          })
          .finally(() => {
            $this.wizard.saving = false;
          });
    },
    // ---- Hero action-bar helpers (delegate to the mounted details component) ----
    quickEmail() {
      const d = this.$refs.details;
      if (d && typeof d.openEmailEditor === 'function') d.openEmailEditor();
    },
    saveDetails() {
      const d = this.$refs.details;
      if (d && typeof d.save === 'function') d.save();
    },
    async copyEmail() {
      if (this.affiliate == null || !this.affiliate.email) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.affiliate.email);
          this.showSuccessNotification(this.affiliate.email);
        }
      } catch (err) {
        console.error('Failed to copy email: ', err);
      }
    }
  }
};
</script>

<style scoped>
/* ===== Tokens (light) ===== */
.am-content {
  --am-bg: #ffffff;
  --am-bg-alt: #f8fafc;
  --am-border: #e2e8f0;
  --am-border-strong: #cbd5e1;
  --am-text: #0f172a;
  --am-text-2: #475569;
  --am-text-muted: #94a3b8;
  --am-primary: #2563eb;
  --am-primary-strong: #1d4ed8;
  --am-primary-soft: #eff6ff;
  --am-success: #16a34a;
  --am-success-soft: #f0fdf4;
  --am-warn: #f59e0b;
  --am-warn-soft: #fffbeb;
  --am-danger: #dc2626;
  --am-danger-soft: #fef2f2;

  padding: 0;
  color: var(--am-text);
}

.dark-layout .am-content {
  --am-bg: #1e293b;
  --am-bg-alt: #0f172a;
  --am-border: #334155;
  --am-border-strong: #475569;
  --am-text: #f1f5f9;
  --am-text-2: #cbd5e1;
  --am-text-muted: #94a3b8;
  --am-primary: #60a5fa;
  --am-primary-strong: #3b82f6;
  --am-primary-soft: rgba(96, 165, 250, 0.15);
  --am-success-soft: rgba(34, 197, 94, 0.15);
  --am-warn-soft: rgba(245, 158, 11, 0.15);
  --am-danger-soft: rgba(239, 68, 68, 0.15);
}

/* ===== NEW-AFFILIATE section head ===== */
.am-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.am-section-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--am-primary-soft);
  color: var(--am-primary);
  font-size: 0.95rem;
}
.am-section-title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: var(--am-text);
}

/* ===== HERO ===== */
.am-hero {
  position: relative;
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 50%, #fef3c7 100%);
  border-radius: 14px;
  padding: 11px 18px 11px;
  margin-bottom: 12px;
  border: 1px solid var(--am-border);
  overflow: hidden;
}

.dark-layout .am-hero {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.18) 0%, rgba(22, 163, 74, 0.12) 50%, rgba(245, 158, 11, 0.12) 100%);
  border-color: var(--am-border);
}

.am-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 0%, rgba(37, 99, 235, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.am-hero-row {
  display: flex;
  align-items: center;
  gap: 13px;
  flex-wrap: wrap;
  position: relative;
}

.am-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 50%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.02rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.am-presence {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid var(--am-bg);
  background: #cbd5e1;
}

.am-avatar.online .am-presence { background: var(--am-success); box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.25); }
.am-avatar.offline .am-presence { background: var(--am-danger); }

.am-hero-name-block { flex: 1; min-width: 0; }

.am-hero-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.am-hero-name-text {
  font-size: 1.08rem;
  font-weight: 800;
  color: var(--am-text);
  letter-spacing: -0.2px;
}

.am-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.am-pill-ok { background: var(--am-success-soft); color: var(--am-success); border: 1px solid rgba(22, 163, 74, 0.25); }
.am-pill-off { background: var(--am-danger-soft); color: var(--am-danger); border: 1px solid rgba(220, 38, 38, 0.25); }

.am-hero-sub {
  display: flex;
  gap: 14px;
  margin-top: 2px;
  font-size: 0.76rem;
  color: var(--am-text-2);
  flex-wrap: wrap;
}

.am-sub-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

/* Stats */
.am-stats { display: flex; gap: 10px; flex-wrap: wrap; }

.am-stat {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid var(--am-border);
  border-radius: 10px;
  padding: 5px 12px;
  min-width: 118px;
  display: flex;
  gap: 9px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.dark-layout .am-stat {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
}

.am-stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.am-stat-cpa .am-stat-icon { background: var(--am-success-soft); color: var(--am-success); }
.am-stat-crg .am-stat-icon { background: var(--am-primary-soft); color: var(--am-primary); }

.am-stat-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--am-text-muted);
}

.am-stat-value {
  font-size: 0.97rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--am-text);
  line-height: 1.15;
}

/* ===== ACTION BAR ===== */
.am-action-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  flex-wrap: wrap;
}

.dark-layout .am-action-bar { border-top-color: rgba(255, 255, 255, 0.08); }

.am-quick-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--am-border);
  color: var(--am-text);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.18s ease;
}

.dark-layout .am-quick-action {
  background: rgba(15, 23, 42, 0.6);
}

.am-quick-action:hover:not(:disabled) {
  background: var(--am-primary-soft);
  color: var(--am-primary);
  border-color: var(--am-primary);
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.18);
}

.am-quick-action:disabled { opacity: 0.45; cursor: not-allowed; }

.am-quick-action i { font-size: 0.85rem; }

.am-action-spacer { flex: 1; min-width: 6px; }

.am-save-btn {
  border-radius: 9px;
  padding: 6px 18px;
  font-weight: 700;
  font-size: 0.82rem;
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.3);
}

/* ===== NEW-AFFILIATE WIZARD ===== */
.am-wiz {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 4px 16px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--am-border);
}
.am-wiz-step {
  display: inline-flex;
  align-items: center;
  gap: 9px;
}
.am-wiz-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 800;
  background: var(--am-bg-alt);
  border: 2px solid var(--am-border-strong);
  color: var(--am-text-muted);
  transition: all 0.2s ease;
}
.am-wiz-label {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--am-text-muted);
}
.am-wiz-step.is-active .am-wiz-dot {
  background: var(--am-primary);
  border-color: var(--am-primary);
  color: #fff;
  box-shadow: 0 3px 10px rgba(37, 99, 235, 0.35);
}
.am-wiz-step.is-active .am-wiz-label { color: var(--am-text); }
.am-wiz-step.is-done .am-wiz-dot {
  background: var(--am-success);
  border-color: var(--am-success);
  color: #fff;
}
.am-wiz-step.is-done .am-wiz-label { color: var(--am-success); }
.am-wiz-line {
  flex: 0 1 70px;
  height: 2px;
  border-radius: 2px;
  background: var(--am-border-strong);
}
.am-wiz-line.is-done { background: var(--am-success); }

.am-new { padding: 4px 2px 2px; }
.am-wiz-cpa { max-width: 640px; margin: 0 auto; }
.am-wiz-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--am-border);
}
</style>

<style>
/* Global rules — b-modal renders outside the scoped component subtree */
.am-modal-header {
  border-bottom: 1px solid #e2e8f0;
}
.dark-layout .am-modal-header {
  border-bottom-color: #334155;
}

/* ===== TABS ===== */
.am-tabs .am-tabs-nav {
  border: none;
  border-bottom: 1px solid var(--am-border, #e2e8f0);
  padding: 0 4px;
  gap: 2px;
}

.am-tabs .am-tabs-nav .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 11px 18px 13px;
  font-size: 0.86rem;
  font-weight: 600;
  color: #94a3b8;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  background: transparent;
  margin-bottom: -1px;
}

.am-tabs .am-tabs-nav .nav-link:hover {
  color: #2563eb;
  background: #eff6ff;
}

.am-tabs .am-tabs-nav .nav-link.active {
  color: #2563eb;
  background: transparent;
  border-bottom: 2px solid #2563eb;
  font-weight: 700;
}

.dark-layout .am-tabs .am-tabs-nav { border-bottom-color: #334155; }
.dark-layout .am-tabs .am-tabs-nav .nav-link:hover {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
}
.dark-layout .am-tabs .am-tabs-nav .nav-link.active {
  color: #60a5fa;
  border-bottom-color: #60a5fa;
}

.am-tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.am-tab-pill i { font-size: 0.88rem; }

.am-tabs .am-tabs-content {
  padding: 18px 6px 6px;
}
</style>
